import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import './styles.scss';
import { BaseModal, Button, Input, Textarea, ToastMessage } from '@cokitchen/cokitchen-components';
import { ReactComponent as SurgeIcon } from '../../assets/images/manage-surge.svg';
import { SURGE_MINUTES, SURGE_REASONS } from '../../helpers/constants';
import useNetworkContext from '../../contexts/network.context';
import { ReactComponent as TooltipImage } from '../../assets/icons/tooltip.svg';
import { toast } from 'react-toastify';

interface SurgeModalProps {
  show: boolean;
  orderCode?: string;
  loading: boolean;
  value: number;
  onClose: () => void;
  onConfirm: (arg0: number, arg1: string[]) => void;
}

const SurgeModal: FC<SurgeModalProps> = ({
  show,
  loading,
  value,
  orderCode = '',
  onClose,
  onConfirm
}) => {
  const { disableNetworkActions } = useNetworkContext();
  const [title, setTitle] = useState('Manage General surge');
  const [activeSurge, setActiveSurge] = useState<number>(0);
  const [comment, setComment] = useState<string>('');
  const [removeSurge, setRemoveSurge] = useState<boolean>(false);
  const [disableCheckbox, setDisableCheckbox] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<string[]>([]);
  const [hoveredTooltip, setHoveredTooltip] = useState('');
  useEffect(() => {
    if (orderCode !== '') setTitle(`Add Surge to #${orderCode.toUpperCase()}`);
    if (+value === 0) {
      setDisableCheckbox(true);
    } else {
      setDisableCheckbox(false);
      setActiveSurge(+value);
    }
  }, [show]);

  const clearForm = () => {
    setSelectedOption([]);
    setActiveSurge(0);
  };
  const emitValue = (): void => {
    const val = removeSurge ? 0 : activeSurge;
    if (activeSurge === 0) {
      toast(<ToastMessage type='error' message={'Please select a surge minute value.'} />);
      onClose();
      return;
    }
    const finalReasons = selectedOption.map((reason) =>
      reason === 'Other' && comment.trim().length > 0 ? comment.trim() : reason
    );
    if (selectedOption.length === 0) {
      toast(
        <ToastMessage
          type='error'
          message={'Please select at least one reason before confirming.'}
        />
      );
      onClose();
      return;
    }
    onConfirm(val, finalReasons);
    clearForm();
  };

  const selectMinute = (minute: number): void => {
    if (disableNetworkActions) return;

    setRemoveSurge(false);
    setActiveSurge(minute);
  };
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    if (checked) {
      setSelectedOption([...selectedOption, name]);
    } else {
      setSelectedOption(selectedOption.filter((o) => o !== name));
    }
  };
  return (
    <BaseModal
      isOpen={show}
      title={title}
      description='Increase estimated time for orders in kitchen'
      titleIcon={<SurgeIcon />}
      titleIconColor='info'
      onRequestClose={onClose}
      footer={
        <Button
          color='primary'
          variant='block'
          className='mx-auto'
          loading={loading}
          onClick={emitValue}
          disabled={disableNetworkActions}>
          Confirm
        </Button>
      }>
      <div className='grid grid-cols-3 gap-8 max-w-[420px]'>
        {SURGE_MINUTES.map((minute) => (
          <span
            key={minute}
            className={`surge-modal__button ${
              activeSurge === minute && !removeSurge ? 'is-active' : ''
            }`}
            role='button'
            onClick={() => selectMinute(minute)}>
            {minute === 0 ? 'None' : `+ ${minute} mins`}
          </span>
        ))}
      </div>
      <div className='my-3'>
        <Input
          type='checkbox'
          label={`Remove${orderCode === '' ? ' general' : ''} surge`}
          onChange={() => setRemoveSurge(!removeSurge)}
          checked={removeSurge}
          disabled={disableCheckbox}
          inline
        />
      </div>
      <div className='mr-16'>
        <p className='mt-6 text-[#1A1A1A] text-xl font-normal'>Add a reason for the surge</p>
        <div
          id='reason-group'
          className=' grid gap-x-11 gap-y-6 grid-cols-2 grid-rows-2 my-2 mx-4 mt-4  '>
          {SURGE_REASONS.map((reason, i) => (
            <div key={i} className='flex items-center gap-2 relative text-xs ]'>
              <Input
                value={value}
                label={reason.label}
                type='checkbox'
                onChange={handleChange}
                name={reason.label}
              />
              {typeof reason.tooltip === 'string' && reason.tooltip.trim() !== '' && (
                <div
                  onMouseOver={() => setHoveredTooltip(reason.label)}
                  onMouseOut={() => setHoveredTooltip('')}
                  className='cursor-pointer ml-auto flex'>
                  <TooltipImage />
                  {hoveredTooltip === reason.label && (
                    <div className=' absolute mt-4 right-10 bg-white text-[#6F7376] text-xs p-4 shadow-lg rounded-lg z-50 overflow-auto '>
                      {reason.tooltip}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {selectedOption.includes('Other') && (
        <div className='mt-2'>
          <Textarea
            maxLength={150}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder='Add a comment...'
            className='w-full text-[#A6A6A6]'
          />
          <span className='text-[#858585] text-xs'> Word limit: {comment.length}/150</span>
        </div>
      )}
    </BaseModal>
  );
};

export default SurgeModal;
